// EN
export const availabilityPrices = {
  header: {
    title: "Availability & Rates",
  },
  common: {
    today: "Today",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
  },
  pricelabs: {
    title: "Prices are being managed by PriceLabs",
    text: "Rates are updated automatically and can’t be edited manually.",
  },
  topbar: {
    calendar: "Calendar",
    bulkEdit: "Bulk edit",
    configTitle: "Calendar settings",
    syncSection: "Synchronization",
    resyncOta: "Re-sync with OTA",
    resyncOtaDesc: "Resends availability and rates to the channel.",
  },
  empty: {
    noHotelTitle: "No hotel-type listings",
    noHotelDesc:
      "Create a <strong>Hotel</strong> or <strong>Camping</strong> listing to start managing availability and rates.",
    goToListings: "Go to listings",
    noRoomsTitle: "{name} has no rooms",
    noRoomsDesc:
      "This listing doesn’t have any rooms registered yet. Add units from the listing detail to manage availability and rates.",
    configureListing: "Configure listing",
  },
  summary: {
    roomState: "Room status",
    roomsForSale: "Rooms for sale",
    netBooked: "Net booked",
  },
  state: {
    available: "Available",
    closed: "Closed",
    soldOut: "Sold out",
    pastLabel: "N/A",
  },
  calendar: {
    // Pluralization (vue-i18n v8 pipes) — choiceIndex = number of rooms / nights
    roomsCount: "{count} room | {count} rooms",
    minStayTooltip: "Minimum stay: {count} night | Minimum stay: {count} nights",
  },
  offBadge: "OFF",
  offTooltip: "Sales off: this rate is not offered on any channel. The room’s other rates keep selling.",
  rateMenu: {
    open: "Rate options",
    minStay: "Minimum stay by date",
    priceLabsMinStay: "PriceLabs manages this rate: its minimum stay is set by PriceLabs.",
    stopSelling: "Stop selling this rate",
    stopping: "Turning sales off…",
    resuming: "Turning sales back on…",
    resumeSelling: "Resume selling this rate",
    derivedPriceHint: "A derived rate takes its price from its parent",
    mainKeepsDerived: "Its derived rates will keep selling",
    mainNotYet: "Closing a main rate is not available yet",
  },
  derived: {
    badge: "D",
    tooltip: "Rate derived from: {parent}{rule}",
    rulePart: " · Rule: {rule}",
  },
  tooltips: {
    syncEnabled:
      "Recalculates and resends availability and maximum stay to the OTA (prevents overbooking).",
    syncDisabled: "Connect the property to a channel to enable syncing.",
    toggleClosedOpen: "Toggle closed/open",
    managedByPricelabs: "Managed by PriceLabs",
    clickToEdit: "Select or click to edit",
  },
  cellTitle: {
    seasonClosed: "Closed by the season «{name}» (edit the season to reopen)",
    pastDate: "Past date",
    closed: "Closed (click to open)",
    detail:
      "{free} free · offering {tope} of {total} · {booked} booked — click to edit the cap",
  },
  soldBadge: {
    confirmed: "{real} confirmed bookings",
    withOverbooking: "{real} confirmed + {over} overbooked",
  },
  bulkModal: {
    from: "From",
    to: "To",
    rangeHint:
      "You can edit the range here to extend or adjust it. (The view will only update the current month visually.)",
    room: "Room",
    whatToEdit: "What to edit",
    optAvail: "Availability and closures",
    optRate: "Prices and minimum stay",
    status: "Status",
    open: "Open",
    close: "Close",
    cap: "cap",
    availHint:
      "This is the total you offer; bookings are deducted automatically and only free rooms are sold. Max. {max} (operational, excluding maintenance).",
    mainRate: "Main rate",
    mainRateHint:
      "Choose the main rate to change; its derived rates adjust automatically.",
    action: "Action",
    price: "Price",
    minStay: "Minimum stay",
    minStayNights: "Minimum stay (nights)",
    minStayHelp: "1 = no restriction (clear)",
  },
  syncConfirm: {
    title: "Sync with the OTA",
    message:
      "This will recalculate and resend this calendar’s availability and maximum stay to the OTA.",
    confirm: "Yes, sync",
  },
  progress: {
    processing: "Processing…",
    syncing: "Syncing calendar with the OTA…",
    updatingAvail: "Updating availability…",
    updatingRate: "Updating rate…",
    applying: "Applying changes…",
    applyingBulk: "Applying bulk edit…",
    applyingIn: "Applying to {name}…",
  },
  toasts: {
    noPropertyToSync: "There is no property to sync.",
    syncOk: "Calendar recalculated and synced with the OTA.",
    syncErr: "Could not sync the calendar with the OTA.",
    loadErr: "Could not load the hotel information.",
    maxAvailClosedBlocked:
      "Maximum available: {capacity} (there are closed, blocked or under-maintenance rooms).",
    availUpdated: "Availability updated",
    availUpdateErr: "Could not update availability",
    freeSuffix: " (free: {free})",
    rateUpdated: "Rate updated",
    rateUpdateErr: "Could not update the rate",
    maxAvailMaintenance:
      "Maximum available: {capacity} (there are rooms under maintenance or not operational).",
    noDatesModifiedBooked: "No dates were modified: {count} with bookings.",
    appliedFreeDates:
      "Applied to the free dates. {count} date(s) with bookings were not modified.",
    changesApplied: "Changes applied successfully",
    savedNotSyncedYet: "Saved here, but it hasn’t reached the sales channels yet. It will retry on its own in a few minutes.",
    cannotEditDerivedPricelabs:
      "You can’t edit a derived rate or one managed by PriceLabs.",
    minStayNumberGte1: "The minimum stay must be a number >= 1.",
    invalidAction: "Invalid action.",
    applyErr: "Could not apply the changes",
    noRoomsToEdit: "There are no rooms to edit.",
    selectAtLeastOneRoom: "Select at least one room.",
    chooseAction: "Choose an action.",
    selectDateRange: "Select a date range.",
    enterValidPrice: "Enter a valid price.",
    minStayGte1: "The minimum stay must be >= 1.",
    appliedWithErrors: "Applied to {ok} room(s). {failed} with an error",
    noDatesModifiedCapacity:
      "No dates were modified: {count} day(s) didn’t allow that number of rooms.",
    bulkAppliedClamped:
      "Bulk edit applied to {ok} room(s). On {clamped} date(s) it was adjusted to the number of rooms in service.",
    bulkApplied: "Bulk edit applied to {ok} room(s).",
    bulkApplyErr: "Could not apply the bulk edit.",
    applyChangesErr: "Could not apply the changes.",
  },
  // Decorative (aria-hidden) empty-state content
  ghost: {
    month: "MAY 2026",
    day1: "Mon 1",
    day2: "Tue 2",
    day3: "Wed 3",
    day4: "Thu 4",
    day5: "Fri 5",
    day6: "Sat 6",
    day7: "Sun 7",
    day8: "Mon 8",
    day9: "Tue 9",
    day10: "Wed 10",
    day11: "Thu 11",
    day12: "Fri 12",
    unit1: "Deluxe Suite",
    unit1Rooms: "(2 rooms)",
    unit2: "Standard Room",
    unit2Rooms: "(3 rooms)",
    baseRate: "Base Rate",
    nonRefundable: "Non-Refundable",
  },
};
